import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import { FiShield, FiArrowRight } from "react-icons/fi";
import { motion } from "framer-motion";

import Footer from "../components/common/Footer";
import ReviewSlider from "../components/common/ReviewSlider";
import CTAButton from "../components/core/HomePage/Button";
import CodeBlocks from "../components/core/HomePage/CodeBlocks";
import HighlightText from "../components/core/HomePage/HighlightText";
import InstructorSection from "../components/core/HomePage/InstructorSection";
import HeroSection from "../components/core/HomePage/HeroSection";
import Course_Slider from "../components/core/Catalog/Course_Slider";
import { getCatalogPageData } from "../services/operations/pageAndComponentData";
import { fadeIn } from "../components/common/motionFrameVarients";
import { DS } from "../utils/designSystem";

const CATEGORY_ID = "658fbf82dd5e9f2da6e24faf";

function SectionHeading({ children, center = true }) {
  return (
    <div className={DS.section[center ? "header" : "headerLeft"]}>
      <h2 className={DS.text.sectionTitle}>{children}</h2>
      <div className={center ? DS.section.underline : DS.section.underlineLeft} />
    </div>
  );
}

function Home() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const [catalogData, setCatalogData] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [credInput, setCredInput] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setCatalogLoading(true);
      const result = await getCatalogPageData(CATEGORY_ID, dispatch);
      setCatalogData(result ?? null);
      setCatalogLoading(false);
    };
    fetchData();
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // undefined while loading, [] when loaded but empty
  const popularCourses = catalogLoading
    ? undefined
    : (catalogData?.mostSellingCourses ?? []);

  return (
    <React.Fragment>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <HeroSection />

      {/* ── Value proposition ────────────────────────────────────────── */}
      <section className={`${DS.layout.section} border-t ${DS.border.divider}`}>
        <div className={DS.layout.container}>
          <motion.div
            variants={fadeIn("up", 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="flex flex-col items-center gap-6 text-center"
          >
            <h2 className={`${DS.text.h2} max-w-2xl`}>
              Empower Your Future with{" "}
              <HighlightText text="Coding Skills" />
            </h2>
            <p className={`${DS.text.sectionSub} max-w-2xl`}>
              Discover an enriching experience on our platform. Find valuable
              information, diverse resources, and tailored courses designed to
              meet your specific needs.
            </p>
            <div className="flex flex-col gap-4 sm:flex-row">
              <CTAButton active={true} linkto="/javascript">
                Javascript Compiler&nbsp;
              </CTAButton>
              <CTAButton active={false} linkto="/python">
                <span className="flex items-center gap-2">
                  Python Compiler <FaArrowRight />
                </span>
              </CTAButton>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Code Block 1 ─────────────────────────────────────────────── */}
      <section className={DS.layout.sectionCompact}>
        <div className={DS.layout.container}>
          <CodeBlocks
            position="lg:flex-row"
            heading={
              <div className="text-3xl lg:text-4xl font-semibold text-richblack-5">
                Unlock your{" "}
                <HighlightText text="coding potential" /> with our online
                courses.
              </div>
            }
            subheading="Our courses are designed and taught by industry experts who have years of experience in coding and are passionate about sharing their knowledge with you."
            ctabtn1={{ btnText: "Try it Yourself", link: "/signup", active: true }}
            ctabtn2={{ btnText: "Learn More",      link: "/about",  active: false }}
            codeColor="text-yellow-25"
            codeblock={`<!DOCTYPE html>\n <html lang="en">\n<head>\n<title>This is myPage</title>\n</head>\n<body>\n<h1><a href="/">Header</a></h1>\n<nav> <a href="/one">One</a> <a href="/two">Two</a> <a href="/three">Three</a>\n</nav>\n</body>`}
            backgroundGradient={<div className="codeblock1 absolute" />}
          />
        </div>
      </section>

      {/* ── Course Sliders ───────────────────────────────────────────── */}
      <section className={`${DS.layout.sectionCompact} border-t ${DS.border.divider}`}>
        <div className={DS.layout.container}>

          {/* Popular picks */}
          <motion.div
            variants={fadeIn("up", 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            <SectionHeading center={false}>
              Popular Picks for You&nbsp;🏆
            </SectionHeading>
            {/* eslint-disable-next-line react/jsx-pascal-case */}
            <Course_Slider Courses={popularCourses} />
          </motion.div>

          {/* Top enrollments */}
          {catalogData?.differentCategory && (
            <motion.div
              variants={fadeIn("up", 0.1)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.1 }}
              className="mt-16"
            >
              <div className={DS.section.headerLeft}>
                <h2 className={DS.text.sectionTitle}>
                  Top Enrollments Today in&nbsp;
                  <HighlightText text={catalogData.differentCategory.name} />
                  &nbsp;✨
                </h2>
                <div className={DS.section.underlineLeft} />
              </div>
              {/* eslint-disable-next-line react/jsx-pascal-case */}
              <Course_Slider Courses={catalogData.differentCategory.courses} />
            </motion.div>
          )}
        </div>
      </section>

      {/* ── Code Block 2 ─────────────────────────────────────────────── */}
      <section className={`${DS.layout.sectionCompact} border-t ${DS.border.divider}`}>
        <div className={DS.layout.container}>
          <CodeBlocks
            position="lg:flex-row-reverse"
            heading={
              <div className="w-full text-3xl lg:text-4xl font-semibold text-richblack-5 lg:w-[50%]">
                Start <HighlightText text="coding in seconds" />
              </div>
            }
            subheading="Go ahead, give it a try. Our hands-on learning environment means you'll be writing real code from your very first lesson."
            ctabtn1={{ btnText: "Continue Lesson", link: "/signup", active: true }}
            ctabtn2={{ btnText: "Learn More",       link: "/about",  active: false }}
            codeColor="text-white"
            codeblock={`import React from "react";\n import CTAButton from "./Button";\nimport TypeAnimation from "react-type";\nimport { FaArrowRight } from "react-icons/fa";\n\nconst Home = () => {\nreturn (\n<div>Home</div>\n)\n}\nexport default Home;`}
            backgroundGradient={<div className="codeblock2 absolute" />}
          />
        </div>
      </section>

      {/* ── Instructor ───────────────────────────────────────────────── */}
      <section className={`${DS.layout.section} border-t ${DS.border.divider}`}>
        <div className={DS.layout.container}>
          <InstructorSection />
        </div>
      </section>

      {/* ── Reviews ──────────────────────────────────────────────────── */}
      <section className={`${DS.layout.sectionCompact} border-t ${DS.border.divider}`}>
        <div className={DS.layout.container}>
          <SectionHeading>Reviews from Our Learners</SectionHeading>
          <ReviewSlider />
        </div>
      </section>

      {/* ── Verify Certificate ───────────────────────────────────── */}
      <section
        id="verify-certificate"
        className={`${DS.layout.section} border-t ${DS.border.divider}`}
      >
        <div className={DS.layout.container}>
          <motion.div
            variants={fadeIn("up", 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="mx-auto max-w-2xl text-center"
          >
            {/* Icon badge */}
            <div className="mb-5 flex justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-caribbeangreen-300/30 bg-caribbeangreen-300/10">
                <FiShield className="text-2xl text-caribbeangreen-300" />
              </div>
            </div>

            <h2 className={`${DS.text.h2} mb-3`}>
              Verify a <HighlightText text="Certificate" />
            </h2>
            <p className={`${DS.text.sectionSub} mb-8`}>
              Enter a KodeVedic credential ID to instantly verify the authenticity
              of any certificate issued on our platform.
            </p>

            {/* Input + button */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const id = credInput.trim();
                if (id) navigate(`/certificate/${id}`);
              }}
              className="flex flex-col gap-3 sm:flex-row"
            >
              <input
                type="text"
                value={credInput}
                onChange={(e) => setCredInput(e.target.value)}
                placeholder="e.g. KV-2025-00001-REACT"
                className="flex-1 rounded-xl border border-richblack-600 bg-richblack-700 px-4 py-3 text-sm text-richblack-5 placeholder-richblack-400 outline-none transition-colors focus:border-caribbeangreen-300"
              />
              <button
                type="submit"
                disabled={!credInput.trim()}
                className="flex items-center justify-center gap-2 rounded-xl bg-caribbeangreen-300 px-6 py-3 text-sm font-semibold text-richblack-900 transition-all hover:bg-caribbeangreen-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Verify <FiArrowRight />
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </React.Fragment>
  );
}

export default Home;
